import "react-draft-wysiwyg/dist/nextjs-draft-wysiwyg.css";
import "./styles.scss";

import { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Editor } from "react-draft-wysiwyg";
import { EditorState } from "draft-js";
import classnames from "classnames";

import FileDropZone from "components/FileDropZone";

import isImageByUrl from "libs/helpers/isImageByUrl";
import withProtocol from "libs/helpers/withProtocol";
import { file_max_size, file_types } from "libs/file_upload_settings";
import { htmlToDraftBlocks, convertToHTML, getPlainText } from "./helpers";

import {
  handleBeforeInput,
  handleEditorStateChange,
  handleKeyCommand,
  handlePastedText,
} from "./editor-helpers";
import useResponsive from "libs/helpers/useResponsive";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as AttachmentIcon } from "assets/icons/new/attachment.svg";
import { ReactComponent as TrashIcon } from "assets/icons/new/trash.svg";
import { ReactComponent as EyeIcon } from "assets/icons/new/eye.svg";

const mainClass = "text-editor";

const TextEditor = ({
  id = "text",
  placeholder,
  content,
  setContent,
  files,
  setFiles,
  attachmentInputId,
  droppedFiles,
  onFilesDropHandled,
  autoSave,
  autoFocus,
  hideActions,
  touched,
  error,
  isDisabled,
  isMessageSending,
  isCompletelyDisabled,
  isSubmitDisabled,
  isInline,
  onPreviewClick,
  onDeleteClick,
  onSubmit,
}) => {
  const { i18n, t } = useTranslation();
  const { max } = useResponsive();

  const editorRef = useRef(null);
  const attachmentsRef = useRef(null);

  const isInitialMount = useRef(true);
  const isCursorPositioned = useRef(false);

  const [isFocused, setIsFocused] = useState(false);
  const isError = touched && error !== undefined;
  const [fileError, setFileError] = useState(null);
  const lastDroppedFiles = useRef(null);
  const fileUploadAvailable =
    files !== undefined && typeof setFiles === "function";
  const canUploadFiles =
    fileUploadAvailable &&
    !autoSave &&
    !isDisabled &&
    !isCompletelyDisabled &&
    !isMessageSending;

  useEffect(() => {
    const editor = editorRef.current;

    if (editor && autoFocus && !max.sm) {
      setTimeout(() => {
        editor.focusEditor();
      }, 0);
    }
  }, [autoFocus, max.sm]);

  useEffect(() => {
    const focusCursorAtEnd = () => {
      if (!content || isCursorPositioned.current) return;

      try {
        const currentContent = content.getCurrentContent();
        const lastBlock = currentContent.getLastBlock();
        const lastKey = lastBlock.getKey();
        const lastLength = lastBlock.getLength();

        const selection = content.getSelection().merge({
          anchorKey: lastKey,
          anchorOffset: lastLength,
          focusKey: lastKey,
          focusOffset: lastLength,
        });

        if (selection !== content.getSelection()) {
          const newEditorState = EditorState.forceSelection(content, selection);
          setContent(newEditorState);
          isCursorPositioned.current = true;
        }
      } catch (error) {
        console.error("Error positioning cursor:", error);
      }
    };

    if (autoFocus && content) requestAnimationFrame(focusCursorAtEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content && content.getCurrentContent().hasText()]);

  useEffect(() => {
    const attachments = attachmentsRef.current;

    if (attachments && files?.length > 0) {
      if (!isInitialMount.current) {
        attachments.scrollLeft = attachments.scrollWidth;
      } else {
        isInitialMount.current = false;
      }
    }
  }, [files]);

  const htmlOnlyParagraphs = convertToHTML(content, true);
  const isFilled = !!htmlOnlyParagraphs;

  const handleSubmit = useCallback(() => {
    if (typeof onSubmit === "function") {
      onSubmit();
    }
  }, [onSubmit]);

  const autoSaveDebouce = useRef(null);
  const prevHtml = useRef(null);

  const handleAutoSave = useCallback(
    (timeout) => {
      if (autoSave && content !== null) {
        const currentHtml = convertToHTML(content);

        if (prevHtml.current !== null) {
          if (currentHtml !== prevHtml.current) {
            clearTimeout(autoSaveDebouce.current);
            if (timeout) {
              autoSaveDebouce.current = setTimeout(() => {
                prevHtml.current = currentHtml;
                handleSubmit();
              }, timeout);
            } else {
              prevHtml.current = currentHtml;
              handleSubmit();
            }
          }
        } else {
          prevHtml.current = currentHtml;
        }
      }
    },
    [autoSave, content, handleSubmit],
  );

  useEffect(() => {
    handleAutoSave(1000);
  }, [handleAutoSave]);

  const handleFiles = useCallback(
    (incomingFiles) => {
      if (!canUploadFiles || !incomingFiles?.length) return;

      const names = new Set(files.map((file) => file.name));
      const errors = {
        size: false,
        extension: false,
        duplicate: false,
      };

      const validFiles = Array.from(incomingFiles).filter((file) => {
        if (file.size > file_max_size * 1024 * 1024) {
          errors.size = true;
          return false;
        }

        const fileExtension = file.name.split(".").pop().toUpperCase();
        if (!file_types.includes(fileExtension)) {
          errors.extension = true;
          return false;
        }

        if (names.has(file.name)) {
          errors.duplicate = true;
          return false;
        }

        names.add(file.name);
        return true;
      });

      if (errors.size) {
        setFileError(t("The file is too large. Allowed maximum size is 20MB."));
      } else if (errors.extension) {
        setFileError(t("Invalid file type. Please select another file."));
      } else if (errors.duplicate) {
        setFileError(t("File already exists. Please choose another one."));
      } else {
        setFileError(null);
      }

      if (validFiles.length > 0)
        setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    },
    [canUploadFiles, files, setFiles, t],
  );

  useEffect(() => {
    if (!droppedFiles || lastDroppedFiles.current === droppedFiles) return;

    lastDroppedFiles.current = droppedFiles;
    handleFiles(droppedFiles);
    onFilesDropHandled();
  }, [droppedFiles, handleFiles, onFilesDropHandled]);

  const handleFilePreview = (file) => {
    if (typeof onPreviewClick !== "function" || !file) return;

    if (typeof file === "object") {
      const url = URL.createObjectURL(file);

      onPreviewClick(url);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 0);
    } else {
      onPreviewClick(file);
    }
  };

  const handleFileDelete = (file) => {
    if (typeof onDeleteClick === "function") onDeleteClick(file);

    setFiles((prevFiles) =>
      prevFiles.filter((prevFile) => prevFile.name !== file.name),
    );
  };

  return (
    <FileDropZone
      id={`editor--${id}`}
      className={classnames(mainClass, {
        [`${mainClass}--focused`]: isFocused,
        [`${mainClass}--error`]: isError,
        [`${mainClass}--inline`]: isInline,
        [`${mainClass}--disabled`]: isDisabled || isCompletelyDisabled,
      })}
      disabled={!canUploadFiles}
      onFilesDrop={handleFiles}
    >
      <div className={`${mainClass}__container`}>
        <Editor
          ref={editorRef}
          localization={{ locale: i18n?.language }}
          placeholder={
            !isFilled ? (placeholder ?? `${t("Write")}...`) : undefined
          }
          editorState={content}
          toolbarClassName={`${mainClass}__toolbar`}
          wrapperClassName={`${mainClass}__wrapper`}
          editorClassName={`${mainClass}__editor`}
          onEditorStateChange={
            typeof setContent === "function" && !isCompletelyDisabled
              ? (editorState) =>
                  handleEditorStateChange(
                    {
                      editorState,
                    },
                    {
                      id,
                      setContent,
                      className: mainClass,
                    },
                  )
              : undefined
          }
          toolbar={
            !isCompletelyDisabled
              ? {
                  options: ["inline", "list", "link", "emoji"],
                  inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline"],
                    bold: { icon: "/wysiwyg/bold.svg", className: undefined },
                    italic: { icon: "/wysiwyg/cur.svg", className: undefined },
                    underline: {
                      icon: "/wysiwyg/under.svg",
                      className: undefined,
                    },
                  },
                  list: {
                    inDropdown: false,
                    options: ["unordered", "ordered"],
                    unordered: {
                      icon: "/wysiwyg/sha_list.svg",
                      className: undefined,
                    },
                    ordered: {
                      icon: "/wysiwyg/num_list.svg",
                      className: undefined,
                    },
                  },
                  link: {
                    inDropdown: false,
                    defaultTargetOption: "_blank",
                    showOpenOptionOnHover: true,
                    options: ["link"],
                  },
                }
              : {
                  options: [],
                }
          }
          toolbarCustomButtons={
            !!attachmentInputId && !isCompletelyDisabled
              ? [
                  <AttachmentButton
                    key="attachment"
                    inputId={attachmentInputId}
                  />,
                ]
              : undefined
          }
          handleKeyCommand={
            !isCompletelyDisabled
              ? (command, editorState) =>
                  handleKeyCommand({ command, editorState }, { setContent })
              : undefined
          }
          handleBeforeInput={
            !isCompletelyDisabled
              ? (character, editorState) =>
                  handleBeforeInput({ character, editorState }, { setContent })
              : undefined
          }
          handlePastedText={
            !isCompletelyDisabled
              ? (text, _, editorState) =>
                  handlePastedText({ text, editorState }, { setContent })
              : undefined
          }
          onFocus={!isCompletelyDisabled ? () => setIsFocused(true) : undefined}
          onBlur={
            !isCompletelyDisabled
              ? () => {
                  handleAutoSave();
                  setIsFocused(false);
                }
              : undefined
          }
        />
        {!autoSave && !hideActions && !max.sm && (
          <Actions
            id={id}
            fileUploadAvailable={fileUploadAvailable}
            handleSubmit={handleSubmit}
            isMessageSending={isMessageSending}
            isSubmitDisabled={isSubmitDisabled}
          />
        )}
      </div>
      {!autoSave && fileUploadAvailable && (
        <>
          <input
            type="file"
            id={`attachments--${id}`}
            multiple={true}
            accept={file_types.map((type) => `.${type}`).join(",")}
            disabled={!canUploadFiles}
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {(files?.length > 0 || fileError) && (
            <div
              className={classnames(`${mainClass}__attachments`, {
                [`${mainClass}__attachments--error`]: !!fileError,
              })}
            >
              <div
                className={`${mainClass}__attachments-inner`}
                ref={attachmentsRef}
              >
                {files.map((file, index) => {
                  const fullFileName = file?.name || "";
                  const fileSize = file?.size || 0;
                  const url = withProtocol(file?.location);
                  const isImage =
                    (file.type && file.type.startsWith("image/")) ||
                    isImageByUrl(url);

                  const lastDotIndex = fullFileName.lastIndexOf(".");
                  const fileName =
                    lastDotIndex !== -1
                      ? fullFileName.substring(0, lastDotIndex)
                      : fullFileName;
                  const fileExt =
                    lastDotIndex !== -1
                      ? fullFileName.substring(lastDotIndex + 1).toUpperCase()
                      : "";

                  const fileSizeMB = fileSize
                    ? (fileSize / (1024 * 1024)).toFixed(2)
                    : "";

                  return (
                    <div key={index} className={`${mainClass}__attachment`}>
                      <div className={`${mainClass}__attachment-preview`}>
                        {isImage ? (
                          <img
                            src={!!url ? url : URL.createObjectURL(file)}
                            alt=""
                            onLoad={
                              !!!url
                                ? (e) => URL.revokeObjectURL(e.target.src)
                                : undefined
                            }
                          />
                        ) : (
                          <span>{fileExt}</span>
                        )}
                      </div>
                      <div className={`${mainClass}__attachment-details`}>
                        <p>{fileName}</p>
                        {fileSizeMB && <p>{fileSizeMB} MB</p>}
                      </div>
                      <div className={`${mainClass}__attachment-actions`}>
                        {isImage && (
                          <button
                            className="preview"
                            type="button"
                            onClick={() =>
                              handleFilePreview(!!url ? url : file)
                            }
                          >
                            <span>{t("Preview")}</span>
                            <EyeIcon />
                          </button>
                        )}
                        <button
                          className="delete"
                          type="button"
                          onClick={() => handleFileDelete(file)}
                        >
                          <span>{t("Delete")}</span>
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!!fileError && (
                  <div
                    className={`${mainClass}__attachment ${mainClass}__attachment--error`}
                  >
                    <div className={`${mainClass}__attachment-details`}>
                      <p>{fileError}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {!autoSave && !hideActions && max.sm && (
        <Actions
          id={id}
          fileUploadAvailable={fileUploadAvailable}
          handleSubmit={handleSubmit}
          isMessageSending={isMessageSending}
          isSubmitDisabled={isSubmitDisabled}
        />
      )}
      {!!isError && <small className={`${mainClass}__error`}>{t(error)}</small>}
    </FileDropZone>
  );
};

const Actions = ({
  id,
  fileUploadAvailable,
  handleSubmit,
  isSubmitDisabled,
  isMessageSending,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={classnames(`${mainClass}__actions`, {
        [`${mainClass}__actions--center`]: !fileUploadAvailable,
      })}
    >
      {fileUploadAvailable && (
        <label className="attachment" htmlFor={`attachments--${id}`}>
          <span>{t("Add attachment")}</span>
          <AttachmentIcon />
        </label>
      )}
      <button
        className="submit"
        type="button"
        disabled={isSubmitDisabled || isMessageSending}
        onClick={handleSubmit}
      >
        <span>{t(isMessageSending ? "Sending..." : "Send")}</span>
        <ArrowIcon />
      </button>
    </div>
  );
};

const AttachmentButton = ({ inputId }) => {
  const { t } = useTranslation();

  return (
    <div className={`${mainClass}__toolbar-attachment`}>
      <label
        className="rdw-option-wrapper"
        htmlFor={inputId}
        title={t("Add attachment")}
        aria-label={t("Add attachment")}
      >
        <AttachmentIcon />
      </label>
    </div>
  );
};

export { htmlToDraftBlocks, convertToHTML, getPlainText };

export default TextEditor;
